import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { postContact } from "@/lib/api";
import { BRAND } from "@/lib/content";

const field =
  "w-full bg-transparent border-b border-[#47394E]/25 py-3 font-body text-[#47394E] outline-none placeholder:text-[#47394E]/40 focus:border-[#B7926A] transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await postContact(form);
      toast.success(res.message);
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="overline text-[#B7926A] mb-6">Contact</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#47394E] leading-[0.95] tracking-tight">
            <MaskedLines lines={["Say hello."]} />
          </h1>
          <p className="font-body text-lg text-[#47394E]/75 mt-8 leading-relaxed">
            Questions about coaching or the Connection Circle? Send a note and Jamie will reply
            personally.
          </p>
          <div className="mt-10 space-y-5">
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 text-[#47394E] hover:text-[#B7926A] transition-colors" data-testid="contact-email-link">
              <span className="rounded-full bg-[#D9C3A8] p-3"><Mail size={18} /></span>
              <span className="font-body">{BRAND.email}</span>
            </a>
            <div className="flex items-center gap-4 text-[#47394E]">
              <span className="rounded-full bg-[#D9C3A8] p-3"><MapPin size={18} /></span>
              <span className="font-body">{BRAND.location}</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#47394E] text-[#E8DCCF] p-12 grain"
              data-testid="contact-success"
            >
              <span className="font-display text-6xl text-[#B7926A]">✦</span>
              <h2 className="font-display text-4xl mt-6">Message sent.</h2>
              <p className="font-body text-[#E8DCCF]/80 mt-4">Thank you — your message is on its way to Jamie.</p>
            </motion.div>
          ) : (
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <input required placeholder="Full name" data-testid="contact-name" value={form.name} onChange={set("name")} className={field} />
                  <input required type="email" placeholder="Email address" data-testid="contact-email" value={form.email} onChange={set("email")} className={field} />
                </div>
                <input placeholder="Subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} className={field} />
                <textarea required rows={5} placeholder="Your message" data-testid="contact-message" value={form.message} onChange={set("message")} className={`${field} resize-none`} />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="rounded-full bg-[#47394E] text-[#E8DCCF] px-10 py-4 font-medium hover:bg-[#B7926A] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send message"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
